import { Collection } from '../models/Collection';

class Emitter {
  private addedEvents: {
    type: string;
    listener: { (evt: Event): void } | { handleEvent(evt: Event): void } | null;
    options?: boolean | { capture?: boolean; once?: boolean; passive?: boolean } | undefined;
  }[];
  public addEventListener: (
    type: string,
    listener: { (evt: Event): void } | { handleEvent(evt: Event): void } | null,
    options?: boolean | { capture?: boolean; once?: boolean; passive?: boolean } | undefined
  ) => void;
  public dispatchEvent: (event: Event) => boolean;
  public removeEventListener: (
    type?: string | undefined,
    callback?: { (evt: Event): void } | { handleEvent(evt: Event): void } | null | undefined,
    options?: boolean | { capture?: boolean } | undefined
  ) => void;
  constructor() {
    this.addedEvents = [];
    const delegate: DocumentFragment = document.createDocumentFragment();
    this.addEventListener = (...args) => {
      const prms = {
        type: args[0] as string,
        listener: args[1] as { (evt: Event): void } | { handleEvent(evt: Event): void } | null,
        options: args[2] as
          | boolean
          | { capture?: boolean; once?: boolean; passive?: boolean }
          | undefined,
      };
      delegate.addEventListener(...args);
      this.addedEvents.push(prms);
    };
    this.dispatchEvent = (...args) => delegate.dispatchEvent(...args);
    this.removeEventListener = (...args) => {
      const prms = {
        type: (args[0] || undefined) as string | undefined,
        callback: (args[1] || undefined) as
          | { (evt: Event): void }
          | { handleEvent(evt: Event): void }
          | null
          | undefined,
        options: (args[2] || undefined) as
          | boolean
          | { capture?: boolean; once?: boolean; passive?: boolean }
          | undefined,
      };
      if (prms.type !== undefined && prms.callback !== undefined) {
        delegate.removeEventListener(prms.type, prms.callback, prms.options);
      } else if (prms.type !== undefined) {
        const events = this.addedEvents.filter((i) => i.type === prms.type);
        events.forEach((i) => {
          try {
            delegate.removeEventListener(i.type, i.listener, i.options);
          } catch (ex) {
            //nothing
          }
        });
        this.addedEvents = this.addedEvents.filter((i) => i.type !== prms.type);
      } else {
        this.addedEvents.forEach((i) => {
          try {
            delegate.removeEventListener(i.type, i.listener, i.options);
          } catch (ex) {
            //nothing
          }
        });
        this.addedEvents = [];
      }
    };
  }
}

// sample class to use Emitter
class CustomEventTarget extends Emitter {}

class _EventTargetContainer {
  private static _instance: _EventTargetContainer;
  private Events: Collection<{
    name: string;
    event: CustomEventTarget;
  }> = new Collection<{
    name: string;
    event: CustomEventTarget;
  }>();

  public static Instance(): _EventTargetContainer {
    if (this._instance) return this._instance;
    this._instance = new this();
    return this._instance;
  }

  public getEventTarget(name: string): CustomEventTarget {
    const evt = this.Events.list().find((i) => i.name === name);
    if (evt) {
      return evt.event;
    } else {
      const evt = { name: name, event: new CustomEventTarget() };
      this.Events.add(evt);
      return evt.event;
    }
  }

  public CreateCustomEvent<T>(eventType: string, details?: T): CustomEvent<T> | undefined {
    if (typeof window !== 'undefined') {
      return new CustomEvent(eventType, { detail: details });
    }
    return undefined;
  }

  public DispatchEvent<T>(evt: CustomEvent<T> | undefined, evtTarget: string): void {
    if (typeof window !== 'undefined' && typeof evt !== 'undefined') {
      this.getEventTarget(evtTarget).dispatchEvent(evt);
    }
  }
}

const EventTargetContainer = _EventTargetContainer.Instance();
export const EventContainer = EventTargetContainer;
